import { SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  SavedPaymentAccountCalculatedTransactions,
  SavedPaymentAccountTransactionItem,
} from "@/api/cashier";
import { LastUpdatedCounter } from "@/components/last-updated-counter/LastUpdatedCounter";
import { ExchangeRate } from "@/components/ExchangeRate";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { BreakdownTransactionList } from "@/pages/withdrawals/summary/breakdown-transaction-list/BreakdownTransactionList";
import type { WithdrawalDataUpdateProps } from "@/pages/withdrawals/summary/types";
import styles from "./BreakdownTransactionCard.module.css";

/** Groups transactions by their processing currency code */
const groupByCurrency = (
  transactions: SavedPaymentAccountTransactionItem[]
): Map<string, SavedPaymentAccountTransactionItem[]> => {
  const groups = new Map<string, SavedPaymentAccountTransactionItem[]>();
  for (const tx of transactions) {
    const code = tx.money_in_processing_currency?.code ?? tx.money.code;
    const group = groups.get(code);
    if (group) {
      group.push(tx);
    } else {
      groups.set(code, [tx]);
    }
  }
  return groups;
};

type UseBreakdownTransactionGroupsParams = WithdrawalDataUpdateProps & {
  breakdown: SavedPaymentAccountCalculatedTransactions;
  collapsed?: boolean;
};

export const useBreakdownTransactionGroups = ({
  breakdown,
  collapsed,
  ...props
}: UseBreakdownTransactionGroupsParams) => {
  const { t } = useTranslation();
  const { currencies } = useCurrencies();

  const tradingCurrencyCode = breakdown.money.code;
  const currency = currencies?.[tradingCurrencyCode];

  const refundGroups = useMemo(
    () => (breakdown.refunds ? groupByCurrency(breakdown.refunds) : new Map()),
    [breakdown.refunds]
  );
  const profitGroups = useMemo(
    () => (breakdown.profits ? groupByCurrency(breakdown.profits) : new Map()),
    [breakdown.profits]
  );

  const refundEntries = Array.from(refundGroups.entries());
  const profitEntries = Array.from(profitGroups.entries());

  // Pre-compute transaction index offsets for continuous numbering across groups
  let cumulativeOffset = 0;
  const refundOffsets = refundEntries.map(([, txs]) => {
    const offset = cumulativeOffset;
    cumulativeOffset += txs.length;
    return offset;
  });
  const profitOffsets = profitEntries.map(([, txs]) => {
    const offset = cumulativeOffset;
    cumulativeOffset += txs.length;
    return offset;
  });

  const renderCurrencyGroup = (
    groupCode: string,
    transactions: SavedPaymentAccountTransactionItem[],
    type: "refunds" | "profits",
    transactionIndexOffset: number
  ) => {
    const isSameCurrency = groupCode === tradingCurrencyCode;
    const groupCurrency = currencies?.[groupCode];

    const titleText =
      type === "refunds"
        ? isSameCurrency
          ? t("withdrawals.summary.breakdown.refunds")
          : t("withdrawals.summary.breakdown.refunds_in", {
              currency: groupCode,
            })
        : isSameCurrency
          ? t("withdrawals.summary.breakdown.profits")
          : t("withdrawals.summary.breakdown.profits_in", {
              currency: groupCode,
            });

    let groupTitle: React.ReactNode;

    if (!isSameCurrency && groupCurrency && currency) {
      const groupExchangeRate =
        transactions.find((tx) => tx.exchange_rate !== undefined)
          ?.exchange_rate ??
        (groupCode === breakdown.money_in_processing_currency?.code
          ? breakdown.exchange_rate
          : undefined);
      const groupExchangeRateUnit = breakdown.exchange_rate_unit ?? 1;

      groupTitle = (
        <div className="ps-flex ps-gap-2 ps-items-center ps-flex-wrap">
          <span>{titleText}</span>
          {groupExchangeRate !== undefined && (
            <>
              {props.isUpdating ? (
                <SkeletonLoader variant="rectangular" width={40} height={6} />
              ) : (
                <span className={styles.exchangeRateText}>
                  ({t("withdrawals.summary.breakdown.exchange_rate")}:{" "}
                  <ExchangeRate
                    unit={groupExchangeRateUnit}
                    currency={currency}
                    exchangeRate={groupExchangeRate}
                    exchangeCurrency={groupCurrency}
                  />
                  )
                </span>
              )}
              <LastUpdatedCounter {...props} hideText />
            </>
          )}
        </div>
      );
    } else {
      groupTitle = titleText;
    }

    const items = transactions.map((tx: SavedPaymentAccountTransactionItem) => {
      return {
        name: tx.name,
        money: isSameCurrency
          ? tx.money
          : (tx.money_in_processing_currency ?? tx.money),
        fee: tx.fee_value,
      };
    });

    return (
      <BreakdownTransactionList
        key={`${type}-${groupCode}`}
        collapsed={collapsed}
        title={groupTitle}
        items={items}
        transactionIndexOffset={transactionIndexOffset}
        itemTestIdPrefix={`breakdown-${type === "refunds" ? "refund" : "profit"}-amount`}
      />
    );
  };

  const refundNodes = refundEntries.map(([code, txs], i) =>
    renderCurrencyGroup(code, txs, "refunds", refundOffsets[i])
  );

  const profitNodes = profitEntries.map(([code, txs], i) =>
    renderCurrencyGroup(code, txs, "profits", profitOffsets[i])
  );

  return { refundNodes, profitNodes };
};
